import React from 'react';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatMultiplier, formatOdds, formatPercent } from './utils';

const RANK_BAR_STYLES = {
  S: { gradientId: 'rd-bar-s', shadowId: 'rd-bar-glow-strong' },
  A: { gradientId: 'rd-bar-a', shadowId: 'rd-bar-glow' },
  B: { gradientId: 'rd-bar-b', shadowId: 'rd-bar-glow' },
  C: { gradientId: 'rd-bar-c', shadowId: '' },
  D: { gradientId: 'rd-bar-d', shadowId: '' },
};

const LEGEND_ITEMS = [
  { label: 'S', dotClassName: 'race-detail-legend-dot-s' },
  { label: 'A', dotClassName: 'race-detail-legend-dot-a' },
  { label: 'B', dotClassName: 'race-detail-legend-dot-b' },
  { label: 'C', dotClassName: 'race-detail-legend-dot-c' },
  { label: 'D', dotClassName: 'race-detail-legend-dot-d' },
];

// 買い目ハイライト時（中央新版）は AI 評価ではなく、買い目（○）かどうかで色分けする。
// 判定は ○ と同じ CSV の pick 列（recommended）を使い、グラフと ○ の基準を揃える
const PICK_BAR_STYLES = {
  pick: { gradientId: 'rd-bar-pick', shadowId: 'rd-bar-glow' },
  other: { gradientId: 'rd-bar-c', shadowId: '' },
};

const PICK_LEGEND_ITEMS = [
  { label: '買い目（○）', dotClassName: 'race-detail-legend-dot-pick' },
  { label: 'その他', dotClassName: 'race-detail-legend-dot-c' },
];

const ValueLabel = ({ x, y, width, value }) => {
  if (value === undefined || value === null) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 10}
      fill="#f4fcff"
      fontSize="12"
      fontWeight="700"
      textAnchor="middle"
    >
      {`${Number(value).toFixed(1)}%`}
    </text>
  );
};

const ChartTooltip = ({ active, payload, label, oddsLabel, showExpectedValue }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  const hasOdds = row?.odds !== undefined && row?.odds !== null;
  return (
    <Box className="race-detail-tooltip">
      <Typography className="race-detail-tooltip-title">
        {row?.mark ? `${row.mark} ` : ''}{label} {row?.horseName}
      </Typography>
      <Typography className="race-detail-tooltip-row">
        勝率 <strong>{formatPercent(row?.winRate, 1)}</strong>
      </Typography>
      {hasOdds && (
        <Typography className="race-detail-tooltip-row">
          {oddsLabel} <strong>{formatOdds(row?.odds)}</strong>
        </Typography>
      )}
      {showExpectedValue && (
        <Typography className="race-detail-tooltip-row">
          期待値 <strong>{formatMultiplier(row?.expectedValue, 2)}</strong>
        </Typography>
      )}
      <Typography className="race-detail-tooltip-row">
        AI評価 <strong>{row?.aiRank}</strong>
      </Typography>
    </Box>
  );
};

function WinRateChart({
  chartData,
  eyebrow = 'WIN PROBABILITY',
  title = '勝率分析（%） 上位8頭',
  oddsLabel = '単勝オッズ',
  highlightPicks = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const legendItems = highlightPicks ? PICK_LEGEND_ITEMS : LEGEND_ITEMS;

  return (
    <Box className="race-detail-panel">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography className="race-detail-section-eyebrow">{eyebrow}</Typography>
          <Typography className="race-detail-section-title">{title}</Typography>
        </Box>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {legendItems.map((item) => (
            <Box key={item.label} className="race-detail-legend-pill">
              <span className={`race-detail-legend-dot ${item.dotClassName}`} />
              {item.label}
            </Box>
          ))}
        </Stack>
      </Stack>

      <Box className="race-detail-chart-shell">
        <ResponsiveContainer width="100%" height={isMobile ? 360 : 420}>
          <BarChart
            data={chartData}
            margin={{ top: 28, right: 12, left: -12, bottom: isMobile ? 18 : 4 }}
          >
            <defs>
              <linearGradient id="rd-bar-s" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#baffea" />
                <stop offset="100%" stopColor="#44eec4" />
              </linearGradient>
              <linearGradient id="rd-bar-a" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#81d9ff" />
                <stop offset="100%" stopColor="#43a7ff" />
              </linearGradient>
              <linearGradient id="rd-bar-b" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ffd985" />
                <stop offset="100%" stopColor="#e3a840" />
              </linearGradient>
              <linearGradient id="rd-bar-c" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2a597b" />
                <stop offset="100%" stopColor="#163148" />
              </linearGradient>
              <linearGradient id="rd-bar-d" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#384556" />
                <stop offset="100%" stopColor="#24303f" />
              </linearGradient>
              <linearGradient id="rd-bar-pick" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ffb3e1" />
                <stop offset="100%" stopColor="#ff4fa3" />
              </linearGradient>
              <filter id="rd-bar-glow-strong" x="-80%" y="-40%" width="260%" height="220%">
                <feGaussianBlur stdDeviation="10" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="rd-bar-glow" x="-80%" y="-40%" width="260%" height="220%">
                <feGaussianBlur stdDeviation="6" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <CartesianGrid stroke="rgba(148, 202, 255, 0.12)" vertical={false} />
            <XAxis
              dataKey="horseNumber"
              tickLine={false}
              axisLine={false}
              tick={{ fill: '#98c9eb', fontSize: isMobile ? 11 : 13, fontWeight: 700 }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={40}
              tick={{ fill: 'rgba(203, 233, 255, 0.78)', fontSize: 12 }}
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip
              content={
                <ChartTooltip oddsLabel={oddsLabel} showExpectedValue={highlightPicks} />
              }
              cursor={{ fill: 'rgba(72, 154, 255, 0.08)' }}
            />
            <Bar dataKey="winRate" radius={[14, 14, 0, 0]} barSize={isMobile ? 22 : 38}>
              <LabelList content={<ValueLabel />} />
              {chartData.map((row) => {
                const style = highlightPicks
                  ? PICK_BAR_STYLES[row.recommended ? 'pick' : 'other']
                  : RANK_BAR_STYLES[row.aiRank] || RANK_BAR_STYLES.C;
                return (
                  <Cell
                    key={`chart-cell-${row.horseNumber}`}
                    fill={`url(#${style.gradientId})`}
                    filter={style.shadowId ? `url(#${style.shadowId})` : undefined}
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

export default WinRateChart;
